
import {defineComponent, computed, ref, onMounted} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import usei18n from '@/core/util/composable/usei18n';
import MonthlyGrid from '@/orangehrmForfaitJoursPlugin/components/MonthlyGrid.vue';

type DayEntry = {
  day: number;
  dayTypeKey: string;
  comment: string | null;
  halfDay: boolean;
  restRespected: boolean;
  source: string;
};

type DeclarationData = {
  id: number;
  status: string;
  empNumber: number;
  year: number;
  month: number;
  signedEmployeeName: string | null;
  signedAt: string | null;
  managerSignedName: string | null;
  managerSignedAt: string | null;
  managerComment: string | null;
  days: DayEntry[];
};

const PDF_API_URL = '/api/v2/forfait-jours/declarations';

export default defineComponent({
  name: 'ManagerSigningPage',
  components: {MonthlyGrid},
  props: {
    declarationId: {type: Number, required: true},
    year: {type: Number, default: () => new Date().getFullYear()},
    month: {type: Number, default: () => new Date().getMonth() + 1},
  },
  setup(props) {
    const {$t} = usei18n();
    const isLoading = ref(true);
    const isSubmitting = ref(false);
    const loadError = ref<string | null>(null);
    const formError = ref<string | null>(null);
    const formSuccess = ref(false);
    const declaration = ref<DeclarationData | null>(null);
    const managerComment = ref('');
    const acknowledged = ref(false);

    const employeeName = computed(() => {
      return declaration.value?.signedEmployeeName ?? '';
    });

    const periodLabel = computed(() => {
      if (!declaration.value) return '';
      return `${String(declaration.value.month).padStart(2, '0')}/${
        declaration.value.year
      }`;
    });

    const statusLabel = computed(() => {
      const status = declaration.value?.status;
      if (!status) return '';
      return $t(`forfait_jours.status_${status.toLowerCase()}`);
    });

    const orderedDays = computed(() => {
      if (!declaration.value?.days) return [];
      return [...declaration.value.days].sort((a, b) => a.day - b.day);
    });

    const http = new APIService(
      // @ts-expect-error: appGlobal is not in window object by default
      window.appGlobal.baseUrl,
      `/api/v2/forfait-jours/my-declarations/${props.declarationId}`,
    );

    async function loadDeclaration(): Promise<void> {
      isLoading.value = true;
      loadError.value = null;
      try {
        const response = await http.request({method: 'GET'});
        if (response?.status >= 400) {
          loadError.value = `Server returned ${response.status}`;
          return;
        }
        const data = response?.data?.data;
        if (data) {
          declaration.value = data as DeclarationData;
        }
      } catch (e) {
        loadError.value = e instanceof Error ? e.message : String(e);
      } finally {
        isLoading.value = false;
      }
    }

    async function onSign(): Promise<void> {
      formError.value = null;
      formSuccess.value = false;

      if (!acknowledged.value) {
        formError.value = $t('forfait_jours.manager_signing_ack_required');
        return;
      }

      isSubmitting.value = true;
      try {
        const signHttp = new APIService(
          // @ts-expect-error: appGlobal is not in window object by default
          window.appGlobal.baseUrl,
          `/api/v2/forfait-jours/declarations/${props.declarationId}/sign`,
        );
        const response = await signHttp.request({
          method: 'POST',
          data: {
            acknowledged: acknowledged.value,
            managerComment: managerComment.value || '',
          },
        });
        if (response?.status >= 400) {
          const msg =
            response?.data?.error?.message ||
            `Server returned ${response.status}`;
          formError.value = msg;
          return;
        }
        // Redirect to roster — no download button shown before redirect
        // @ts-expect-error: appGlobal is not in window object by default
        window.location.href = `${window.appGlobal.baseUrl}/forfaitJours/viewManagerRoster`;
      } catch (e) {
        formError.value = e instanceof Error ? e.message : String(e);
      } finally {
        isSubmitting.value = false;
      }
    }

    function onBack(): void {
      // @ts-expect-error: appGlobal is not in window object by default
      const baseUrl = window.appGlobal.baseUrl;
      window.location.href = `${baseUrl}/forfaitJours/viewManagerRoster`;
    }

    async function onDownloadPdf(): Promise<void> {
      try {
        const pdfHttp = new APIService(
          // @ts-expect-error: appGlobal is not in window object by default
          window.appGlobal.baseUrl,
          `${PDF_API_URL}/${props.declarationId}/pdf`,
        );
        const response = await pdfHttp.request({
          method: 'GET',
          params: {kind: 'signed'},
        });
        if (response?.data?.data?.contentBase64) {
          const byteStr = atob(response.data.data.contentBase64);
          const byteArray = new Uint8Array(byteStr.length);
          for (let i = 0; i < byteStr.length; i++) {
            byteArray[i] = byteStr.charCodeAt(i);
          }
          const blob = new Blob([byteArray], {type: 'application/pdf'});
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = response.data.data.filename || 'declaration.pdf';
          a.click();
          URL.revokeObjectURL(url);
        }
      } catch (e) {
        formError.value = e instanceof Error ? e.message : String(e);
      }
    }

    onMounted(() => {
      void loadDeclaration();
    });

    return {
      isLoading,
      isSubmitting,
      loadError,
      formError,
      formSuccess,
      declaration,
      employeeName,
      periodLabel,
      statusLabel,
      managerComment,
      acknowledged,
      orderedDays,
      onSign,
      onBack,
      onDownloadPdf,
    };
  },
});
